import api from '../api';
import { PhonemeProfile, PhonemeSkillMap, PaginatedResponse } from './types';

const baseURL = '/speaking/v1'

class PhonemeService {
    // ============== PHONEME MANAGEMENT ==============

    async getPhonemes(filters?: {
        category?: string;
        place_of_articulation?: string;
        symbol?: string;
        page?: number;
        page_size?: number;
    }): Promise<PaginatedResponse<PhonemeProfile>> {
        const params = new URLSearchParams();

        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    params.append(key, value.toString());
                }
            });
        }

        const response = await api.get(`${baseURL}/phonemes/?${params}`);
        return response.data;
    }

    async getPhonemeById(phonemeId: string): Promise<PhonemeProfile> {
        const response = await api.get(`${baseURL}/phonemes/${phonemeId}/`);
        return response.data;
    }

    async getPhonemeSkillMap(): Promise<PhonemeSkillMap> {
        const response = await api.get(`${baseURL}/phoneme-skill-map/`);
        return response.data;
    }

    // ============== PHONEME ANALYSIS ==============

    analyzePhonemeError(targetPhoneme: string, detectedPhoneme: string): {
        errorType: 'substitution' | 'deletion' | 'insertion' | 'distortion';
        severity: 'minor' | 'moderate' | 'major';
        suggestions: string[];
    } {
        // Simple rule-based error analysis
        if (targetPhoneme === detectedPhoneme) {
            return {
                errorType: 'substitution',
                severity: 'minor',
                suggestions: []
            };
        }

        // Common substitution patterns
        const commonSubstitutions: Record<string, string[]> = {
            '/r/': ['/w/', '/l/'],
            '/θ/': ['/f/', '/s/'],
            '/ð/': ['/d/', '/z/'],
            '/ʃ/': ['/s/', '/tʃ/'],
            '/ʒ/': ['/z/', '/dʒ/'],
            '/tʃ/': ['/ʃ/', '/t/'],
            '/dʒ/': ['/ʒ/', '/d/']
        };

        const errorType = 'substitution'; // Default to substitution
        let severity: 'minor' | 'moderate' | 'major' = 'moderate';
        const suggestions: string[] = [];

        // Determine severity based on common error patterns
        if (commonSubstitutions[targetPhoneme]?.includes(detectedPhoneme)) {
            severity = 'moderate';
            suggestions.push(`Common error: ${targetPhoneme} → ${detectedPhoneme}`);
        } else {
            severity = 'major';
            suggestions.push(`Unexpected substitution: ${targetPhoneme} → ${detectedPhoneme}`);
        }

        // Add specific suggestions based on phoneme characteristics
        suggestions.push(...this.getPhonemeSpecificSuggestions(targetPhoneme, detectedPhoneme));

        return {
            errorType,
            severity,
            suggestions
        };
    }

    getPhonemeSpecificSuggestions(targetPhoneme: string, detectedPhoneme: string): string[] {
        const suggestions: string[] = [];

        // R-sound corrections
        if (targetPhoneme === '/r/' && detectedPhoneme === '/w/') {
            suggestions.push(
                "Try curling your tongue back and raising the sides",
                "Practice 'red', 'road', 'bring' with tongue curl",
                "Keep your lips neutral, don't round them"
            );
        }

        // TH-sound corrections
        if (targetPhoneme === '/θ/' && detectedPhoneme === '/f/') {
            suggestions.push(
                "Put your tongue between your teeth",
                "Blow air between tongue and teeth, not lips and teeth",
                "Practice 'think', 'thin', 'three' with tongue out"
            );
        }

        // SH-sound corrections
        if (targetPhoneme === '/ʃ/' && detectedPhoneme === '/s/') {
            suggestions.push(
                "Round your lips slightly and pull tongue back",
                "Make the 'shh' sound like quieting someone",
                "Practice 'ship', 'wish', 'sure' with rounded lips"
            );
        }

        // L-sound corrections
        if (targetPhoneme === '/l/' && detectedPhoneme === '/r/') {
            suggestions.push(
                "Touch your tongue tip to the roof of your mouth",
                "Keep tongue tip up and let air flow around the sides",
                "Practice 'light', 'late', 'tell' with tongue up"
            );
        }

        return suggestions;
    }

    // ============== PHONEME CATEGORIES ==============

    getPhonemesByCategory(category: 'vowel' | 'consonant' | 'diphthong' | 'schwa'): Promise<PhonemeProfile[]> {
        return this.getPhonemes({ category }).then(response => response.results);
    }

    getPhonemesByArticulation(place: string, manner?: string): Promise<PhonemeProfile[]> {
        const filters: any = { place_of_articulation: place };
        if (manner) filters.manner_of_articulation = manner;

        return this.getPhonemes(filters).then(response => response.results);
    }

    // ============== PHONEME DIFFICULTY ==============

    getPhonemeDifficulty(phoneme: string, nativeLanguage: string = 'unknown'): {
        level: 'easy' | 'medium' | 'hard';
        reason: string;
        commonErrors: string[];
    } {
        // Difficulty mapping based on common L2 learning challenges
        const difficultyMap: Record<string, {
            level: 'easy' | 'medium' | 'hard';
            reason: string;
            commonErrors: string[];
        }> = {
            '/r/': {
                level: 'hard',
                reason: 'Complex tongue position and movement',
                commonErrors: ['/w/', '/l/']
            },
            '/θ/': {
                level: 'hard',
                reason: 'Requires tongue between teeth',
                commonErrors: ['/f/', '/s/', '/t/']
            },
            '/ð/': {
                level: 'hard',
                reason: 'Voiced th-sound with tongue between teeth',
                commonErrors: ['/d/', '/z/', '/v/']
            },
            '/ʃ/': {
                level: 'medium',
                reason: 'Tongue retraction with lip rounding',
                commonErrors: ['/s/', '/tʃ/']
            },
            '/ʒ/': {
                level: 'medium',
                reason: 'Voiced sh-sound',
                commonErrors: ['/z/', '/dʒ/']
            },
            '/tʃ/': {
                level: 'medium',
                reason: 'Affricate requiring precise timing',
                commonErrors: ['/ʃ/', '/t/']
            },
            '/dʒ/': {
                level: 'medium',
                reason: 'Voiced affricate',
                commonErrors: ['/ʒ/', '/d/']
            },
            '/l/': {
                level: 'medium',
                reason: 'Lateral tongue position',
                commonErrors: ['/r/', '/w/']
            }
        };

        // Language-specific adjustments
        if (nativeLanguage === 'japanese' || nativeLanguage === 'korean') {
            if (phoneme === '/r/' || phoneme === '/l/') {
                return {
                    level: 'hard',
                    reason: 'L/R distinction not present in native language',
                    commonErrors: difficultyMap[phoneme]?.commonErrors || []
                };
            }
        }

        if (nativeLanguage === 'spanish') {
            if (phoneme === '/θ/' || phoneme === '/ð/') {
                return {
                    level: 'hard',
                    reason: 'TH sounds not present in most Spanish dialects',
                    commonErrors: difficultyMap[phoneme]?.commonErrors || []
                };
            }
        }

        return difficultyMap[phoneme] || {
            level: 'easy',
            reason: 'Generally straightforward to produce',
            commonErrors: []
        };
    }

    // ============== PHONEME PRACTICE RECOMMENDATIONS ==============

    getPhonemeExerciseRecommendations(phoneme: string): {
        minimalPairs: string[][];
        practiceWords: string[];
        targetSentences: string[];
        exercises: string[];
    } {
        const recommendations: Record<string, any> = {
            '/r/': {
                minimalPairs: [
                    ['red', 'led'], ['right', 'light'], ['grow', 'glow'],
                    ['crowd', 'cloud'], ['free', 'flee'], ['pray', 'play']
                ],
                practiceWords: ['red', 'road', 'bring', 'tree', 'great', 'very', 'sorry', 'three'],
                targetSentences: [
                    'The red car crossed the road.',
                    'Three great trees grew near the river.',
                    'Sorry, I forgot to bring the right book.'
                ],
                exercises: [
                    'Tongue curl exercise',
                    'R vs W minimal pairs',
                    'R-controlled vowels practice',
                    'Consonant cluster practice'
                ]
            },
            '/θ/': {
                minimalPairs: [
                    ['think', 'sink'], ['three', 'free'], ['thumb', 'sum'],
                    ['thin', 'tin'], ['math', 'mass'], ['path', 'pass']
                ],
                practiceWords: ['think', 'three', 'thumb', 'north', 'math', 'path', 'month', 'both'],
                targetSentences: [
                    'I think three things are important.',
                    'The path through the north woods is thin.',
                    'Both math problems were about growth.'
                ],
                exercises: [
                    'Tongue placement between teeth',
                    'TH vs F distinction',
                    'Voiceless TH practice',
                    'Word-final TH sounds'
                ]
            },
            '/ʃ/': {
                minimalPairs: [
                    ['ship', 'sip'], ['wash', 'was'], ['wish', 'with'],
                    ['shop', 'sop'], ['sure', 'sour'], ['push', 'pus']
                ],
                practiceWords: ['ship', 'shop', 'wish', 'wash', 'sure', 'push', 'fresh', 'finish'],
                targetSentences: [
                    'She wishes to shop for fresh fish.',
                    'Push the ship away from the shore.',
                    'I\'m sure the dish was delicious.'
                ],
                exercises: [
                    'Lip rounding practice',
                    'SH vs S distinction',
                    'Tongue retraction',
                    'SH in different positions'
                ]
            }
        };

        return recommendations[phoneme] || {
            minimalPairs: [],
            practiceWords: [],
            targetSentences: [],
            exercises: []
        };
    }

    // ============== HELPER METHODS ==============

    formatPhonemeSymbol(symbol: string): string {
        // Ensure proper IPA formatting
        if (!symbol.startsWith('/') || !symbol.endsWith('/')) {
            return `/${symbol}/`;
        }
        return symbol;
    }

    getPhonemeColor(category: string): string {
        const colors: Record<string, string> = {
            'vowel': '#E3F2FD',
            'consonant': '#FFF3E0',
            'diphthong': '#F3E5F5',
            'schwa': '#E8F5E8'
        };
        return colors[category] || '#F5F5F5';
    }

    groupPhonemesByFeature(phonemes: PhonemeProfile[]): Record<string, PhonemeProfile[]> {
        const grouped: Record<string, PhonemeProfile[]> = {};

        phonemes.forEach(phoneme => {
            const key = phoneme.place_of_articulation || 'other';
            if (!grouped[key]) {
                grouped[key] = [];
            }
            grouped[key].push(phoneme);
        });

        return grouped;
    }
}

const phonemeService = new PhonemeService();
export default phonemeService; 